/* eslint-disable no-nested-ternary */

import { FaCircle, FaPlus } from 'react-icons/fa';

import { CardData } from '../types/CardData';
import { GameFormat } from '../types/GameFormat';
import { Hole } from '../types/Hole';
import { Totals } from '../types/Totals';
import { Tournament } from '../types/Tournament';
import {
  DEFAULT_STABLEFORD_POINTS,
  calculatePops,
  calculateStablefordScore,
  getCountBestBallScoresToTake,
  getFormattedScore,
  getHoleNumberForCourse,
  isGameFormatSplit,
  isStablefordFormat,
} from '../utils/utils';

export default function MultiplayerLeaderboardDetails({
  event,
  players,
  isNet,
  team,
  isRinger = false,
  rounds = [],
  showTotals = false,
  format,
}: {
  event: Tournament;
  players: CardData[];
  isNet: boolean;
  team: CardData;
  isRinger?: boolean;
  rounds?: Tournament[];
  showTotals?: boolean;
  format: GameFormat;
  previousRoundTotals?: Totals | null;
}) {
  if (isGameFormatSplit(format)) {
    players.sort((a: CardData, b: CardData) => {
      const aGameIndex =
        event.splitGameFormats?.findIndex((f: GameFormat) => a.format === f) ??
        -1;
      const bGameIndex =
        event.splitGameFormats?.findIndex((f: GameFormat) => b.format === f) ??
        -1;
      return aGameIndex - bGameIndex;
    });
  }

  function getHoleNumber(hole: number) {
    return (players[0] as CardData).courseType === 'Back'
      ? hole + 1 + 9
      : hole + 1;
  }

  function getRoundName(eventCode: number) {
    if (rounds !== null && rounds !== undefined && rounds.length > 0) {
      const round = rounds.find((r) => r.eventCode === eventCode);
      if (round !== null && round !== undefined) {
        return round.name;
      }
    }
    return '';
  }

  function holeHeaders(startIndex: number, endIndex: number) {
    const headers = [];
    for (let i = startIndex; i < endIndex; i++) {
      let startingHole = '';
      if (getHoleNumber(i) === (players[0] as CardData).startingHole) {
        startingHole = 'bg-primary text-white';
      }
      headers.push(
        <th
          key={i}
          className={`w-5 text-xs font-semibold text-center tracking-normal md:w-10 sm:w-6 ${startingHole}`}
        >
          {getHoleNumberForCourse(event.selectedCourse, getHoleNumber(i))}
        </th>
      );
    }
    return headers;
  }

  function legendItem(text: string, color: string) {
    return (
      <div className="flex mr-4">
        <div className={`w-4 h-4 mr-1 bg-opacity-25 ${color}`}></div>
        {text}
      </div>
    );
  }

  function scoreToParColor(par: number, score: number) {
    if (score === null || score === undefined) {
      return '';
    }
    if (par === score + 1) {
      // Birdie
      return 'bg-opacity-25 bg-green-500';
    }
    if (par >= score + 2) {
      // Eagle
      return 'bg-opacity-25 bg-yellow-500';
    }
    if (par === score - 1) {
      // Bogey
      return 'bg-opacity-25 bg-red-500';
    }
    if (par <= score - 2) {
      // Double Bogey+
      return 'bg-opacity-25 bg-blue-500';
    }
    return '';
  }

  function grayOutSplitRoundHoles(holeNumber: number, subFormat: string) {
    if (isGameFormatSplit(format)) {
      if (
        (event.splitGameFormats as GameFormat[])[
          Math.floor(
            (holeNumber - 1) / (format === GameFormat.SixSixSix ? 6 : 9)
          )
        ] !== subFormat
      ) {
        return 'bg-gray-200';
      }
    }
    return '';
  }

  function dots(hole: Hole, player: CardData) {
    if (event.isGrossOnly) {
      return null;
    }

    let pops = calculatePops(player, hole.number ?? 1);

    if (format === GameFormat.ThreesFoursFives && hole.par === 5) {
      pops = 0;
    }

    const dotsArr = [];

    for (let i = 0; i < pops; i++) {
      dotsArr.push(
        <FaCircle key={i} className="float-right mr-[2px] text-[4px]" />
      );
    }

    if (pops < 0) {
      dotsArr.push(<FaPlus className="float-right mr-[2px] text-xxxs" />);
    }
    return <div className="h-2 absolute top-[2px] right-0">{dotsArr}</div>;
  }

  function getThreesFoursFivesHandicap() {
    const par5Count =
      team.scores.filter((val: Hole) => val.par === 5).length ?? 0;

    const p1 = players[0] as CardData;
    const p2 = players[1] as CardData;
    const lowHandicap = p1.handicap < p2.handicap ? p1.handicap : p2.handicap;
    const highHandicap = p1.handicap > p2.handicap ? p1.handicap : p2.handicap;
    const scrambleHandicap =
      Math.round(
        ((lowHandicap * 35 + highHandicap * 15) *
          (par5Count / team.scores.length)) /
          10
      ) / 10;

    return scrambleHandicap;
  }

  function nineHoleTable(startIndex: number, endIndex: number) {
    const is18Holes = team.scores.length > 9 && endIndex === 18;
    const isGrossNetFormat =
      format === GameFormat.OneGrossOneNet ||
      format === GameFormat.OneGrossOneNetStableford ||
      format === GameFormat.OneGrossTwoNet ||
      format === GameFormat.OneGrossTwoNetStableford;

    let parTotal = 0;

    const startIndexOffset = event.courseType === 'Back' ? 9 : 0;

    const overallGrossScore = team.scores.reduce((acc, hole) => {
      return acc + (team.totals?.grossScores[hole.number as number] ?? 0);
    }, 0);

    const overallNetScore = team.scores.reduce((acc, hole) => {
      return acc + (team.totals?.netScores[hole.number as number] ?? 0);
    }, 0);

    const overallPar = team.scores.reduce((acc, hole) => {
      return acc + hole.par;
    }, 0);

    const overallGrossPoints = team.scores.reduce((acc, hole) => {
      return acc + (team.totals?.grossPoints[hole.number as number] ?? 0);
    }, 0);

    const overallNetPoints = team.scores.reduce((acc, hole) => {
      return acc + (team.totals?.netPoints[hole.number as number] ?? 0);
    }, 0);

    return (
      <table>
        <thead>
          <tr className="border">
            <th className="p-1 text-xs font-semibold text-center tracking-normal"></th>
            {isGameFormatSplit(format) && (
              <th className="p-1 text-xs font-semibold text-center tracking-normal"></th>
            )}
            {holeHeaders(startIndex, endIndex)}
            <th className="p-1 text-xs font-semibold text-center tracking-normal">
              {team.scores[startIndex]?.number === 1 ? 'Out' : 'In'}
            </th>
            {isStablefordFormat(format) && (
              <th className="p-1 text-xs font-semibold text-center tracking-normal">
                Pts
              </th>
            )}
            {is18Holes && (
              <>
                <th className="p-1 text-xs font-semibold text-center tracking-normal">
                  Tot
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          <tr className="border">
            <td className="p-1 text-xs text-center tracking-normal">Par</td>
            {isGameFormatSplit(format) && (
              <td className="w-5 p-1 text-xs font-semibold text-center tracking-normal"></td>
            )}
            {players[0]?.scores.slice(startIndex, endIndex).map((hole, i) => {
              parTotal += hole.par;
              return (
                <td
                  key={i}
                  className="text-xs text-center tracking-normal md:w-10 sm:w-6"
                >
                  {hole.par}
                </td>
              );
            })}
            <td className="p-1 text-xs text-center tracking-normal">
              {parTotal}
            </td>
            {isStablefordFormat(format) && (
              <td className="p-1 text-xs text-center tracking-normal "></td>
            )}
            {is18Holes && (
              <td className="p-1 text-xs text-center tracking-normal">
                {overallPar}
              </td>
            )}
          </tr>
          {players.map((player: CardData, i: number) => {
            const playerOverallGrossScore = player.scores.reduce(
              (acc, hole) => {
                return acc + (hole.grossScore as number);
              },
              0
            );

            const playerOverallNetScore = player.scores.reduce((acc, hole) => {
              const pops = calculatePops(player, hole.number ?? 1);
              const holeScore = hole.grossScore;

              if (holeScore === null) {
                return acc;
              }

              return acc + holeScore - pops;
            }, 0);

            let grossTotal = 0;
            let netTotal = 0;
            let grossPoints = 0;
            let netPoints = 0;
            return (
              <tr className="border" key={i}>
                <td className="p-1 text-xs text-center tracking-normal">
                  {isRinger
                    ? `${player.name} ${
                        player.name !== 'Aggregate'
                          ? `(${getRoundName(player.eventCode ?? 0)})`
                          : ''
                      }`
                    : `${player.name}
                  ${
                    !event.isGrossOnly
                      ? `(${
                          player.handicap >= 0
                            ? player.handicap
                            : `+${player.handicap * -1}`
                        })`
                      : ''
                  }
                  `}
                </td>
                {isGameFormatSplit(format) && (
                  <td className="text-xxs font-semibold text-center tracking-normal">
                    {player.format}
                  </td>
                )}
                {player.scores.slice(startIndex, endIndex).map((hole) => {
                  let pops = calculatePops(player, hole.number ?? 1);
                  if (
                    format === GameFormat.ThreesFoursFives &&
                    hole.par === 5
                  ) {
                    pops = 0;
                  }

                  if (hole.grossScore !== null) {
                    grossTotal += hole.grossScore as number;
                    netTotal += (hole.grossScore as number) - pops;
                    grossPoints += calculateStablefordScore(
                      (hole.grossScore as number) - hole.par,
                      event.stablefordPoints ?? DEFAULT_STABLEFORD_POINTS
                    );
                    netPoints += calculateStablefordScore(
                      (hole.grossScore as number) - pops - hole.par,
                      event.stablefordPoints ?? DEFAULT_STABLEFORD_POINTS
                    );
                  }

                  return (
                    <td
                      key={hole.number}
                      className={`border relative ${
                        isGameFormatSplit(format) ? 'w-2' : 'w-4'
                      } text-xs text-center tracking-normal md:w-10 sm:w-6 ${
                        hole.taken ? 'font-extrabold' : ''
                      } ${scoreToParColor(
                        hole.par,
                        isNet
                          ? hole.grossScore
                            ? hole.grossScore - pops
                            : (hole.grossScore as number)
                          : (hole.grossScore as number)
                      )} ${grayOutSplitRoundHoles(
                        hole.number ?? 0,
                        player.format
                      )}`}
                    >
                      {dots(hole, player)}
                      {hole.grossScore}
                    </td>
                  );
                })}
                <td className="p-1 text-xs text-center tracking-normal">
                  {isNet ? netTotal : grossTotal}
                </td>
                {isStablefordFormat(format) && (
                  <td className="p-1 text-xs text-center tracking-normal border">
                    {isNet ? netPoints : grossPoints}
                  </td>
                )}
                {is18Holes && (
                  <td className="p-1 text-xs text-center tracking-normal">
                    {isNet ? playerOverallNetScore : playerOverallGrossScore}
                  </td>
                )}
              </tr>
            );
          })}

          {showTotals ? (
            isStablefordFormat(format) ? (
              <tr className="border">
                <td className="p-1 text-xs text-center tracking-normal">
                  {isGrossNetFormat ? '' : isNet ? 'Net' : 'Gross'} Points
                </td>
                {isGameFormatSplit(format) && (
                  <td className="w-5 p-1 text-xs font-semibold text-center tracking-normal"></td>
                )}
                {team.scores.slice(startIndex, endIndex).map((_hole, j) => {
                  return (
                    <td
                      key={j}
                      className={`w-4 text-xs text-center tracking-normal md:w-10 sm:w-6 border`}
                    >
                      {isNet
                        ? team.totals.netPoints[
                            startIndex + startIndexOffset + j + 1
                          ]
                        : team.totals.grossPoints[
                            startIndex + startIndexOffset + j + 1
                          ]}
                    </td>
                  );
                })}
                <td
                  className={`w-4 text-xs text-center tracking-normal md:w-10 sm:w-6 border`}
                ></td>
                <td className="p-1 text-xs text-center tracking-normal">
                  {isNet
                    ? team.totals.netPoints
                        .slice(
                          startIndex + startIndexOffset + 1,
                          endIndex + startIndexOffset + 1
                        )
                        .reduce((a, b) => a + b, 0)
                    : team.totals.grossPoints
                        .slice(
                          startIndex + startIndexOffset + 1,
                          endIndex + startIndexOffset + 1
                        )
                        .reduce((a, b) => a + b, 0)}
                </td>
                {is18Holes && (
                  <td className="p-1 text-xs text-center tracking-normal">
                    {isNet ? overallNetPoints : overallGrossPoints}
                  </td>
                )}
              </tr>
            ) : (
              <tr className="border">
                <td className="p-1 text-xs text-center tracking-normal">
                  {isGrossNetFormat || event.startAtHandicap
                    ? 'Score'
                    : isNet
                    ? 'Net'
                    : 'Gross'}
                  {format === GameFormat.ThreesFoursFives &&
                    ` (${getThreesFoursFivesHandicap()})`}
                </td>
                {isGameFormatSplit(format) && (
                  <td className="w-5 p-1 text-xs font-semibold text-center tracking-normal"></td>
                )}
                {team.scores.slice(startIndex, endIndex).map((_hole, j) => {
                  if (
                    team.totals.netScores[
                      startIndex + startIndexOffset + j + 1
                    ] == null ||
                    team.totals.grossScores[
                      startIndex + startIndexOffset + j + 1
                    ] == null
                  ) {
                    return (
                      <td
                        key={j}
                        className={`w-4 text-xs text-center tracking-normal md:w-10 sm:w-6 border`}
                      ></td>
                    );
                  }
                  return (
                    <td
                      key={j}
                      className={`w-4 text-xs text-center tracking-normal md:w-10 sm:w-6 border`}
                    >
                      {getFormattedScore(
                        isNet
                          ? team.totals.netToParScores[
                              startIndex + startIndexOffset + j + 1
                            ] ?? 0
                          : team.totals.grossToParScores[
                              startIndex + startIndexOffset + j + 1
                            ] ?? 0
                      )}
                    </td>
                  );
                })}
                <td className="p-1 text-xs text-center tracking-normal">
                  {isNet
                    ? team.totals.netScores
                        .slice(
                          startIndex + startIndexOffset + 1,
                          endIndex + startIndexOffset + 1
                        )
                        .reduce((a, b) => a + b, 0)
                    : team.totals.grossScores
                        .slice(
                          startIndex + startIndexOffset + 1,
                          endIndex + startIndexOffset + 1
                        )
                        .reduce((a, b) => a + b, 0)}
                </td>
                {is18Holes && (
                  <td className="p-1 text-xs text-center tracking-normal">
                    {isNet ? overallNetScore : overallGrossScore}
                  </td>
                )}
              </tr>
            )
          ) : null}
        </tbody>
      </table>
    );
  }

  const scoresToTake = getCountBestBallScoresToTake(format);
  const handicapPercentage = scoresToTake / players.length;
  const totalTeamHandicap = players.reduce((a, b) => a + b.handicap, 0);
  const teamHandicap = Math.round(totalTeamHandicap * handicapPercentage);

  return (
    <>
      <div className="text-xs tracking-tighter text-gray-700 flex justify-center items-center flex-col">
        {event.startAtHandicap &&
          (isStablefordFormat(format)
            ? `Team Quota: ${
                team.scores.length * 2 * getCountBestBallScoresToTake(format) -
                teamHandicap
              }`
            : null)}{' '}
        {/* :  `Starting Score: ${teamHandicap * -1}`)} */}
      </div>
      <div className="flex justify-center items-center flex-col xl:flex-row">
        {nineHoleTable(0, 9)}
        {(players[0] as CardData).scores.length > 9
          ? nineHoleTable(9, (players[0] as CardData).scores.length)
          : null}
      </div>
      <div className="flex text-xs mt-4 justify-center">
        {legendItem('Eagle+', 'bg-yellow-500')}
        {legendItem('Birdie', 'bg-green-500')}
        {legendItem('Bogey', 'bg-red-500')}
        {legendItem('Double Bogey+', 'bg-blue-500')}
      </div>
    </>
  );
}
